---
title: "Position Sizing: The 1–2% Rule and Calculating Lot Size"
slug: position-sizing
shortTitle: "Position Sizing"
description: "How to size every trade from the amount you are willing to lose: the 1–2% rule, the position size formula, and step-by-step lot sizes for Bitcoin and gold."
level: intermediate
category: trading
course: intermediate
order: 9
topics: [risk-management, leverage-margin]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: true
cover: /academy/img/covers/position-sizing.svg
faqs:
  - q: "Do I have to follow the 1–2% rule?"
    a: "No, it is a widely used guideline rather than a rule anyone enforces. Its purpose is to keep any single loss, and any losing streak, small enough to recover from. Many traders start at 1% or less while they are learning."
  - q: "Should I base the percentage on my balance or my equity?"
    a: "Many traders use equity, because it includes the profit or loss on positions that are already open. If you have several open trades, basing new risk on equity stops you sizing up from gains that have not been banked."
  - q: "What if the minimum lot size risks more than my limit?"
    a: "Then the trade does not fit your plan at that stop distance. Skip it, or look for a setup with a closer stop that still makes sense. Moving the stop closer just to make the size fit usually means it gets hit by normal noise."
  - q: "Does higher leverage mean I am risking more?"
    a: "Not by itself. Your risk is the stop distance times your position size; leverage only changes how much margin the position ties up. The danger is that high leverage makes it easy to open a position far larger than your plan allows."
  - q: "How do I allow for fees, spread and slippage?"
    a: "Treat your risk figure as a ceiling and leave a small buffer. The spread and the trade fee add to every losing trade, and a stop loss can fill beyond its price in a fast market or a gap, so the real loss can be a little larger than the calculation."
---

Most traders spend their time on entries: which market, which pattern, which moment. Yet the decision that does the most to keep you trading is far less exciting: **how big the position is**. Position size decides how much a losing trade costs you, and losing trades are a normal part of trading for everyone.

This lesson shows how to size every trade from the amount you are willing to lose. You will learn the 1–2% guideline, the position size formula, and how to turn the result into lots on {{BRAND}}, with full worked examples for Bitcoin and gold.

<Warning title="Size first, leverage second">

CFDs are leveraged, and leverage magnifies losses as well as gains. Many traders lose money trading CFDs. Position sizing is how you decide in advance what a losing trade will cost, so set it before you choose leverage, and never risk money you cannot afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **Risk a fixed slice.** Decide what share of your account you will lose if the stop is hit, commonly 1–2% or less.
- **Size comes from the stop.** Units = risk in USDT ÷ stop distance; lots = units ÷ contract size.
- **Leverage sets margin, not risk.** The loss at your stop is the same at 5x or 20x; leverage only changes the margin tied up.
- **Round down and check.** Round to the lot step, then confirm the **Loss (USDT)** figure in the order panel before you trade.

</KeyTakeaways>

## Why the size of a loss matters so much

Losses compound against you. After a 10% loss you need an 11% gain to get back to where you started; after a 50% loss you need 100%. Big individual losses, or a run of medium ones, can put an account in a hole that takes months to climb out of. Keeping each loss small is what gives you time to learn.

## The 1–2% rule

The guideline is simple: on any single trade, risk no more than 1–2% of your account. Risk here means the amount you lose if your **stop loss** is hit, not the position's value. On a 5,000 USDT account, 1% is 50 USDT.

<CompareTable caption="Ten losses in a row, starting from 5,000 USDT (illustrative)">

| Risk per trade | Account after 10 losses | Share left | Gain needed to recover |
| --- | --- | --- | --- |
| 1% | 4,522 USDT | 90.4% | +10.6% |
| 2% | 4,085 USDT | 81.7% | +22.4% |
| 5% | 2,994 USDT | 59.9% | +67.0% |
| 10% | 1,743 USDT | 34.9% | +186.8% |

</CompareTable>

The table assumes each loss is a fixed percentage of the account at that moment, so 1% of a smaller account is a smaller amount. Ten losses in a row is uncommon but entirely possible, and the difference between rows is the difference between an inconvenience and a wipe-out.

## The position size formula

Every calculation follows the same three steps:

1. **Risk amount** = account equity × risk %
2. **Units** = risk amount ÷ stop distance (in USDT per unit)
3. **Lots** = units ÷ contract size, rounded **down** to the lot step

The **stop distance** is the gap between your entry and your stop loss. The **contract size** is how many units one lot represents, which differs by market: 1 lot of gold is 100 oz, for example. The order panel shows it for every market.

<Example title="BTC/USDT: a buy (illustrative prices)">

Account: 5,000 USDT. Risk: 1%. Buy at 60,000 with a stop at 58,800. Say 1 lot = 1 BTC with a step of 0.01 lots.

- Risk amount: 5,000 × 1% = **50 USDT**
- Stop distance: 60,000 − 58,800 = **1,200 USDT** per BTC
- Units: 50 ÷ 1,200 = 0.0417 BTC
- Lots: 0.0417 ÷ 1 = 0.0417, rounded down to **0.04 lots**
- Actual risk: 0.04 × 1,200 = **48 USDT**
- Position value (notional): 0.04 × 60,000 = 2,400 USDT
- Margin at 10x leverage: 2,400 ÷ 10 = 240 USDT
- Value of a 1 USDT move: 0.04 USDT

</Example>

<Example title="GOLD/USDT: a sell (illustrative prices)">

Account: 5,000 USDT. Risk: 1%. Sell at 2,400 with a stop at 2,415. 1 lot = 100 oz; say the step is 0.01 lots (1 oz).

- Risk amount: 5,000 × 1% = **50 USDT**
- Stop distance: 2,415 − 2,400 = **15 USDT** per oz
- Units: 50 ÷ 15 = 3.33 oz
- Lots: 3.33 ÷ 100 = 0.0333, rounded down to **0.03 lots** (3 oz)
- Actual risk: 3 × 15 = **45 USDT**
- Position value (notional): 3 × 2,400 = 7,200 USDT
- Margin at 20x leverage: 7,200 ÷ 20 = 360 USDT
- Value of a 1 USDT move: 3 USDT

</Example>

{/* TODO: verify with platform — BTC contract size and the min / step for BTC and GOLD; examples assume 1 lot = 1 BTC and a 0.01-lot step. */}

<CompareTable caption="The two calculations side by side">

| | BTC/USDT buy | GOLD/USDT sell |
| --- | --- | --- |
| Risk amount (1% of 5,000) | 50 USDT | 50 USDT |
| Stop distance | 1,200 per BTC | 15 per oz |
| Units | 0.0417 BTC | 3.33 oz |
| Lots (rounded down) | 0.04 | 0.03 |
| Actual risk | 48 USDT | 45 USDT |
| Notional | 2,400 USDT | 7,200 USDT |
| Margin (example leverage) | 240 USDT at 10x | 360 USDT at 20x |

</CompareTable>

Notice that the gold position is worth more than the whole account, yet risks less than the Bitcoin trade. A tight stop allows a larger position for the same risk. That is fine as long as the stop is honored; it is also why a gap through a tight stop can hurt more than expected.

## Leverage does not set your risk

Take the Bitcoin trade above. At 5x leverage it needs 2,400 ÷ 5 = 480 USDT of margin; at 20x, 2,400 ÷ 20 = 120 USDT. In both cases the loss at the stop is 48 USDT. Leverage, up to {{MAX_LEVERAGE}}, only changes how much margin is set aside.

The catch is that leverage makes oversized positions easy to open. And because {{BRAND}} uses **cross margin**, all your positions share the same account equity. If losses push your margin level down to {{STOP_OUT_LEVEL}}, positions are closed automatically. [Leverage and Margin Explained Simply](/academy/article/leverage-and-margin) covers this in depth.

## Setting the size in the order panel

<Steps>
<Step title="Switch the size to Lots">

Use the **USDT / Lots** toggle. Check the **Contract size** and **Min / step** rows for this market.

</Step>
<Step title="Enter the volume">

Type your rounded lot size or use the **−** and **+** buttons. The panel shows the **Units** and the **Notional** value.

</Step>
<Step title="Add the stop loss and check the loss">

Turn on **TP / SL** and enter your **SL Price**. The **Loss (USDT)** field shows what you would lose at that price. It should be at or below your risk amount.

</Step>
<Step title="Read the summary">

Check **Margin required**, **Free margin after** and **Value of $1 move** before you confirm.

</Step>
</Steps>

<Screenshot name="order-panel-volume-lots" alt="The order panel in Lots mode showing contract size, min and step, units and notional" caption="In Lots mode, the panel shows the contract size, min / step, units and notional for your volume." />

<ProTip>

If you size in USDT instead of lots, you can still check your risk: enter the stop price and read **Loss (USDT)**. If it is above your limit, reduce the amount until it fits. Whichever way you size, that one number is your final check.

</ProTip>

## When the numbers do not fit

- **The minimum lot is too big.** If even the smallest size risks more than your limit, skip the trade rather than accept extra risk.
- **Costs and slippage.** The spread, the trade fee ({{TRADE_FEE}}) and any swap add to losses, and a stop can fill beyond its price in a fast market. Leave a small buffer.
- **Related positions.** A 1% long on BTC and a 1% long on ETH are close to one 2% bet, because the two often move together. Many traders cap the total risk of all open trades.
- **Volatile markets.** Wider stops mean smaller positions. [Volume and Volatility](/academy/article/volume-and-volatility) shows how to set stop distances with ATR.

## Conclusion

Position sizing turns risk management from a feeling into a number. Choose the share of your account you are willing to lose, measure the distance to your stop, divide one by the other and convert to lots using the market's contract size, rounding down. Leverage then decides only how much margin the trade uses. Check **Loss (USDT)** before every order, keep related positions in mind, and the size of any single loss stays within the limit you chose. [Risk/Reward Ratio and Win Rate](/academy/article/risk-reward-and-win-rate) shows how that limit fits into the bigger math of trading.
